<template>
  <div>
    <b-sidebar
      id="sidebar-details"
      bg-variant="white"
      v-model="visibility"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Details</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility = false"
        />
      </div>
      <b-form class="p-2">
        <b-row v-if="currentItem" class="">
          <b-col v-if="currentItem.student" md="12" class="mb-1">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                  size="48"
                  :src="getPicturePreview(currentItem.student.picture)"
                  variant="light-primary"
                  referrerpolicy="no-referrer"
                />
              </template>

              <b-badge variant="light-primary">
                {{ currentItem.student.name }}
              </b-badge>

              <span
                class="font-weight-bold d-block text-nowrap"
                style="margin: 4px 0px"
              >
                <b-badge variant="light-primary">
                  F/N: {{ currentItem.student.father_name }}
                </b-badge>
              </span>

              <small class="text-muted">
                <b-badge variant="light-primary">
                  GR - {{ currentItem.student.grNo }}
                </b-badge>
                <b-badge variant="light-primary" style="margin-inline: 10px">
                  {{ currentItem.student.meta_class }}
                </b-badge>
              </small>
            </b-media>
          </b-col>
          <b-col v-else-if="currentItem.staff" md="12" class="mb-1">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                  size="48"
                  :src="getPicturePreview(currentItem.staff.picture)"
                  variant="light-primary"
                  referrerpolicy="no-referrer"
                />
              </template>

              <b-link class="font-weight-bold d-block text-nowrap mb-25">
                <b-badge variant="light-primary">
                  {{ currentItem.staff.name }}
                </b-badge>
              </b-link>
              <small class="text-muted">
                <b-badge variant="light-primary">
                  {{ currentItem.staff.designation }}
                </b-badge>
              </small>
            </b-media>
          </b-col>
          <b-col md="12" class="mt-1">
            <h5 class="font-weight-bolder mb-0">
              Bill# {{ currentItem.order.id }}
            </h5>
            <hr />
            <ul class="list-unstyled">
              <li
                class="d-flex justify-content-between mb-25"
                v-for="(item, ind) in currentItem.details"
                :key="item.id"
              >
                <div class="">
                  <span class="">{{ item.productName }} ({{ item.qty }})</span>
                </div>
                <div v-if="!hideSellingPrice" class="">
                  Rs.{{ item.total }}
                </div>
              </li>
            </ul>
            <hr />
            <ul v-if="!hideSellingPrice" class="list-unstyled">
              <li class="d-flex justify-content-between mb-25">
                <div class="font-weight-bolder">Total</div>
                <div class="font-weight-bolder">
                  Rs.{{ currentItem.order.netTotal }}
                </div>
              </li>
              <li class="d-flex justify-content-between mb-25">
                <div class="font-weight-bolder">Received</div>
                <div class="font-weight-bolder">
                  Rs.{{ currentItem.order.rcvd }}
                </div>
              </li>
              <li class="d-flex justify-content-between mb-25">
                <div class="font-weight-bolder">Balance</div>
                <div class="font-weight-bolder">
                  Rs.{{ currentItem.order.balance }}
                </div>
              </li>
              <hr />
            </ul>
            <div>
              Note:
              <p>{{ currentItem.order.note }}</p>
            </div>
          </b-col>
        </b-row>
      </b-form>

      <sidebar-content />
    </b-sidebar>

    <b-card>
      <!-- Other domains: full inventory report filters -->
      <b-row
        v-if="!supportsStockLogReport && reportsVisible.includes(reportDomain)"
        class="mb-sm-0 mb-1"
      >
        <b-col md="4" sm="6">
          <b-form-group label="Report Type">
            <v-select
              v-model="filterObj.type"
              :options="reportTypes"
              :reduce="(val) => val.value"
              label="label"
              :clearable="false"
              placeholder="Select report type"
            />
          </b-form-group>
        </b-col>

        <b-col md="3" sm="6">
          <b-form-group label="Campus">
            <v-select
              v-model="filterObj.campus"
              :options="[{ id: 0, branch: 'All Campuses' }, ...campuses]"
              :reduce="(val) => val.id"
              label="branch"
              :clearable="false"
              placeholder="Select campus"
            />
          </b-form-group>
        </b-col>

        <b-col md="3" sm="6">
          <b-form-group label="Date">
            <flat-pickr
              v-model="filterObj.rangeDate"
              :config="config"
              class="form-control"
              :placeholder="$t('Select Date')"
              @on-change="setDate"
            />
          </b-form-group>
        </b-col>

        <b-col md="2" sm="6" class="mt-sm-2">
          <b-button variant="primary" class="btn-icon" @click="showReport">
            <span>Show Report</span>
          </b-button>
        </b-col>
      </b-row>
      <b-row>
        <b-col class="d-flex align-items-center" md="12">
          <b-form-group class="w-100">
            <b-input-group>
              <b-input-group-prepend is-text>
                <feather-icon icon="SearchIcon" />
              </b-input-group-prepend>

              <b-form-input
                v-model.trim="searchQuery"
                :placeholder="$t('Search') + '...'"
              >
              </b-form-input>
              <b-input-group-append v-if="supportsStockLogReport">
                <b-button
                  variant="primary"
                  class="text-nowrap"
                  :disabled="stockLogLoading"
                  @click="openStockLogModal"
                >
                  <b-spinner v-if="stockLogLoading" small class="mr-50" />
                  <span>Show Log Report</span>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <b-table
        class="mt-1"
        ref="selectableTable"
        :items="filteredItems"
        :fields="visibleTransactionFields"
        :current-page="currentPage"
        :per-page="perPage"
        :busy="dataLoading"
        show-empty
        responsive
        hover
        small
      >
        <template #table-busy>
          <div
            class="d-flex justify-content-center mb-3"
            style="margin-top: 50px"
          >
            <b-spinner
              style="width: 3rem; height: 3rem"
              type="grow"
              variant="primary"
              label="Spinning"
            ></b-spinner>
          </div>
        </template>

        <template #cell(bill)="data">
          <b-badge variant="light-primary">
            {{ data.item.order.id }}
          </b-badge>
        </template>

        <template #cell(details)="data">
          <b-media v-if="data.item.student" vertical-align="center">
            <template #aside>
              <b-avatar
                size="48"
                :src="getPicturePreview(data.item.student.picture)"
                variant="light-primary"
                referrerpolicy="no-referrer"
              />
            </template>

            <b-badge variant="light-primary" class="mb-25">
              {{ data.item.student.name }}
            </b-badge>

            <span class="font-weight-bold d-block text-nowrap mb-25">
              <b-badge variant="light-primary">
                F/N: {{ data.item.student.father_name }}
              </b-badge>
            </span>

            <small class="text-muted">
              <b-badge variant="light-primary">
                GR - {{ data.item.student.grNo }}
              </b-badge>
              <b-badge variant="light-primary" class="ml-50">
                {{ data.item.student.meta_class }}
              </b-badge>
            </small>
          </b-media>
          <b-media v-else-if="data.item.staff" vertical-align="center">
            <template #aside>
              <b-avatar
                size="44"
                :src="getPicturePreview(data.item.staff.picture)"
                variant="light-primary"
                referrerpolicy="no-referrer"
              />
            </template>

            <b-link class="font-weight-bold d-block text-nowrap mb-25">
              <b-badge variant="light-primary">
                {{ data.item.staff.name }}
              </b-badge>
            </b-link>
            <small class="text-muted">
              <b-badge variant="light-primary">
                {{ data.item.staff.designation }}
              </b-badge>
            </small>
          </b-media>
        </template>

        <template #cell(rcvdDate)="data">
          <b-badge variant="light-primary">
            {{
              new Date(data.item.order.rcvdDate).toLocaleDateString("en-UK", {
                year: "numeric",
                day: "numeric",
                month: "short",
              })
            }}
          </b-badge>
        </template>

        <template #cell(netTotal)="data">
          <b-badge variant="light-primary">
            {{ data.item.order.netTotal }}
          </b-badge>
        </template>
        <template #cell(rcvd)="data">
          <b-badge variant="light-primary">
            {{ data.item.order.rcvd }}
          </b-badge>
        </template>
        <template #cell(balance)="data">
          <b-badge variant="light-primary">
            {{ data.item.order.balance }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <div class="">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="outline-primary"
              v-b-tooltip.hover.left
              title="Print"
              class="btn-icon mr-1"
              @click="printReceipt(data.item.order)"
            >
              <feather-icon icon="PrinterIcon" class="" />
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              v-b-tooltip.hover.left
              title="View Details"
              class="btn-icon mr-1"
              @click="openDetails(data.item)"
            >
              <feather-icon icon="EyeIcon" class="" />
            </b-button>
            <b-button
              v-if="rights.delete"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="outline-danger"
              v-b-tooltip.hover.left
              title="Delete Order"
              class="btn-icon"
              @click="deleteOrder(data.item.order)"
            >
              <feather-icon icon="TrashIcon" class="" />
            </b-button>
          </div>
        </template>
      </b-table>
      <b-col cols="12" class="d-flex align-items-center justify-content-center">
        <b-pagination
          v-model="currentPage"
          :total-rows="filteredItems.length"
          :per-page="perPage"
          first-number
          last-number
          class="mb-0 mt-1 mt-sm-0"
          prev-class="prev-item"
          next-class="next-item"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
        </b-pagination>
      </b-col>
    </b-card>

    <!-- Stock Log Report filters (myskoolpublicschool only) -->
    <b-modal
      id="stock-log-report-modal"
      v-model="stockLogModal"
      title="Stock Log Report"
      size="lg"
      centered
      hide-footer
      no-close-on-backdrop
    >
      <b-row>
        <b-col md="6" class="mb-1">
          <b-form-group label="Status" label-class="font-weight-bold">
            <v-select
              v-model="stockLogFilters.status"
              :options="stockLogStatusOptions"
              :reduce="(val) => val.value"
              label="label"
              :clearable="false"
              placeholder="All Statuses"
            />
          </b-form-group>
        </b-col>
        <b-col md="6" class="mb-1">
          <b-form-group label="Category" label-class="font-weight-bold">
            <v-select
              v-model="stockLogFilters.categoryID"
              :options="[{ id: 0, name: 'All Categories' }, ...categories]"
              :reduce="(val) => val.id"
              label="name"
              :clearable="false"
              placeholder="All Categories"
              @input="onStockLogCategoryChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="6" class="mb-1">
          <b-form-group label="Product" label-class="font-weight-bold">
            <v-select
              v-model="stockLogFilters.productID"
              :options="[{ id: 0, name: 'All Products' }, ...filteredProducts]"
              :reduce="(val) => val.id"
              :get-option-label="productOptionLabel"
              :clearable="false"
              placeholder="All Products"
            />
          </b-form-group>
        </b-col>
        <b-col md="6" class="mb-1">
          <b-form-group label="Warehouse" label-class="font-weight-bold">
            <v-select
              v-model="stockLogFilters.warehouseID"
              :options="[{ id: 0, name: 'All Warehouses' }, ...warehouses]"
              :reduce="(val) => val.id"
              label="name"
              :clearable="false"
              placeholder="All Warehouses"
            />
          </b-form-group>
        </b-col>
        <b-col md="6" class="mb-1">
          <b-form-group label="Supplier" label-class="font-weight-bold">
            <v-select
              v-model="stockLogFilters.supplierID"
              :options="[{ id: 0, name: 'All Suppliers' }, ...suppliers]"
              :reduce="(val) => val.id"
              label="name"
              :clearable="false"
              placeholder="All Suppliers"
            />
          </b-form-group>
        </b-col>
        <b-col md="6" class="mb-1">
          <b-form-group label="Date Range" label-class="font-weight-bold">
            <flat-pickr
              v-model="stockLogFilters.rangeDate"
              :config="config"
              class="form-control"
              :placeholder="$t('Select Date')"
              @on-change="setStockLogDate"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <div class="d-flex justify-content-end mt-1" style="gap: 0.5rem">
        <b-button variant="outline-secondary" @click="stockLogModal = false">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="stockLogLoading"
          @click="openStockLogReport"
        >
          <b-spinner v-if="stockLogLoading" small class="mr-50" />
          Generate Report
        </b-button>
      </div>
    </b-modal>
  </div>
</template>
<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BLink,
  BBadge,
  BPagination,
  BImg,
  VBTooltip,
  BFormCheckbox,
  BFormGroup,
  BForm,
  BMediaAside,
  BSpinner,
  BSidebar,
  VBToggle,
  BInputGroupPrepend,
  BInputGroupAppend,
  BFormTextarea,
  BInputGroup,
  BDropdown,
  BDropdownItem,
  BFormSpinbutton,
  BAvatar,
  BModal,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { mapActions } from "vuex";
import { getMediaPreviewUrl } from "@/utils/gdriveUpload";

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BLink,
    BBadge,
    BPagination,
    BImg,
    VBTooltip,
    BFormCheckbox,
    BFormGroup,
    BForm,
    BMediaAside,
    BSpinner,
    BSidebar,
    VBToggle,
    BInputGroupPrepend,
    BInputGroupAppend,
    BFormTextarea,
    BInputGroup,
    BDropdown,
    BDropdownItem,
    BFormSpinbutton,
    BAvatar,
    BModal,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
    "b-toggle": VBToggle,
    Ripple,
  },
  data() {
    return {
      currentItem: null,
      visibility: false,
      items: [],
      details: [],
      fields: [
        { label: "bill#", key: "bill" },
        { label: "details", key: "details" },
        { label: "date", key: "rcvdDate" },
        { label: "total", key: "netTotal" },
        { label: "received", key: "rcvd" },
        { label: "balance", key: "balance" },
        { key: "actions", label: "actions" },
      ],
      perPage: 30,
      currentPage: 1,
      searchQuery: "",
      dataLoading: false,
      campuses: [],
      categories: [],
      products: [],
      warehouses: [],
      suppliers: [],
      stockLogModal: false,
      stockLogLoading: false,
      stockLogStatusOptions: [
        { label: "All Statuses", value: "" },
        { label: "Stock-In", value: "stock-in" },
        { label: "Stock-Out", value: "stock-out" },
      ],
      stockLogFilters: {
        productID: 0,
        categoryID: 0,
        warehouseID: 0,
        supplierID: 0,
        status: "",
        rangeDate: null,
        dateFrom: "",
        dateTo: "",
      },
      filterObj: {
        campus: 0,
        rangeDate: null,
        fromDate: null,
        toDate: null,
        type: "stock-sales",
      },
      config: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
        mode: "range",
      },
      reportDomain: this.$store.state.report.replace(/_/g, ""),
      rights: {},
      reportsVisible: [
        "myskoolstjoseph",
        "myskoolstjacob",
        "myskoolprofectus",
        "myskoolsmartdha",
        "myskoolelixir",
        "myskoolcrestviewacademy",
        "myskool",
        "myskoolpublicschool",
      ],
      stockLogDomains: ["myskoolpublicschool"],
    };
  },
  computed: {
    hideSellingPrice() {
      return this.reportDomain === "myskoolpublicschool";
    },
    supportsStockLogReport() {
      return this.stockLogDomains.includes(this.reportDomain);
    },
    reportTypes() {
      // Stock Log is myskoolpublicschool-only via dedicated button (not these dropdowns)
      return [
        { label: "Sales Transactions", value: "stock-sales" },
        { label: "Stock Report", value: "stock-report" },
        { label: "Voucher Summary Report", value: "voucher-summary-report" },
      ];
    },
    filteredProducts() {
      const list = Array.isArray(this.products) ? this.products : [];
      const catId = Number(this.stockLogFilters.categoryID) || 0;
      if (!catId) return list;
      return list.filter((p) => Number(p.categoryID || p.catID) === catId);
    },
    visibleTransactionFields() {
      if (!this.hideSellingPrice) return this.fields;
      return this.fields.filter(
        (f) => !["netTotal", "rcvd", "balance"].includes(f.key)
      );
    },
    filteredItems() {
      if (!this.searchQuery) return this.items;

      const query = this.searchQuery.toLowerCase().trim();
      return this.items.filter((item) => {
        if (item.staff) {
          return item.staff.name.toLowerCase().includes(query);
        } else if (item.student) {
          return (
            item.student.name.toLowerCase().includes(query) ||
            item.student.grNo.toString().includes(query)
          );
        }
        return false;
      });
    },
  },
  created() {
    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({
        name: "misc-not-authorized",
      });
    } else {
      this.rights = this.$store.state.rights[this.$route.name];
      this.LoadData();

      if (this.reportsVisible.includes(this.reportDomain)) {
        this.LoadCampuses();
        const dt = new Date().toJSON().split("T")[0];
        this.filterObj = {
          campus: this.$store.state.userData.cId || 0,
          rangeDate: `${dt} to ${dt}`,
          fromDate: dt,
          toDate: dt,
          type: "stock-sales",
        };
        this.stockLogFilters.rangeDate = `${dt} to ${dt}`;
        this.stockLogFilters.dateFrom = dt;
        this.stockLogFilters.dateTo = dt;
      }
    }
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    getPicturePreview(url) {
      if (!url) return "";
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },

    productOptionLabel(opt) {
      if (!opt) return "";
      if (opt.id === 0) return opt.name || "All Products";
      return opt.name || opt.productName || opt.title || "";
    },

    printReceipt(order) {
      const url =
        `https://${this.reportDomain}.myskool.app/Inventory/Slip?` +
        `dbb=${this.$store.state.userData.db}` +
        `&cID=${order.campusID}` +
        `&orderID=${order.id}`;

      window.open(url, "_blank");
    },

    openDetails(item) {
      this.currentItem = { ...item };
      this.visibility = true;
    },

    async LoadData() {
      this.dataLoading = true;
      var obj = {
        url:
          this.$store.state.domain +
          "posOrders/LoadData?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.items = await this.get(obj);
      this.dataLoading = false;
    },

    async LoadCampuses() {
      const obj = {
        url:
          this.$store.state.domain +
          "campuses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.campuses = await this.get(obj);
    },

    async loadStockLogLookups() {
      const db = this.$store.state.userData.db;
      const cID = this.$store.state.userData.cId;
      const token = this.$store.state.userData.token;
      const [categories, products, warehouses, suppliers] = await Promise.all([
        this.get({
          url: `${this.$store.state.domain}posCategories?db=${db}&cID=${cID}`,
          token,
        }),
        this.get({
          url: `${this.$store.state.domain}posStock?db=${db}&cID=${cID}`,
          token,
        }),
        this.get({
          url: `${this.$store.state.domain}posWarehouse/LoadData?db=${db}&cID=0`,
          token,
        }),
        this.get({
          url: `${this.$store.state.domain}posSupplier?db=${db}&cID=${cID}`,
          token,
        }),
      ]);
      this.categories = Array.isArray(categories) ? categories : [];
      this.products = Array.isArray(products) ? products : [];
      this.warehouses = Array.isArray(warehouses) ? warehouses : [];
      this.suppliers = Array.isArray(suppliers) ? suppliers : [];
    },

    onStockLogCategoryChange() {
      this.stockLogFilters.productID = 0;
    },

    setDate(sd, dateStr) {
      if (dateStr) {
        if (sd.length > 1) {
          let date = dateStr.split(" to ");
          if (date.length == 1) {
            this.filterObj.fromDate = date[0];
            this.filterObj.toDate = date[0];
          } else {
            this.filterObj.fromDate = date[0];
            this.filterObj.toDate = date[1];
          }
        } else {
          this.filterObj.fromDate = dateStr;
          this.filterObj.toDate = null;
        }
      } else {
        this.filterObj.fromDate = null;
        this.filterObj.toDate = null;
      }
    },

    setStockLogDate(sd, dateStr) {
      if (dateStr) {
        if (sd.length > 1) {
          const date = dateStr.split(" to ");
          this.stockLogFilters.dateFrom = date[0];
          this.stockLogFilters.dateTo = date[1] || date[0];
        } else {
          this.stockLogFilters.dateFrom = dateStr;
          this.stockLogFilters.dateTo = dateStr;
        }
      } else {
        this.stockLogFilters.dateFrom = "";
        this.stockLogFilters.dateTo = "";
      }
    },

    async openStockLogModal() {
      this.stockLogLoading = true;
      try {
        await this.loadStockLogLookups();
        this.stockLogModal = true;
      } catch (e) {
        console.error(e);
        this.$bvToast.toast("Failed to load stock log filters.", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.stockLogLoading = false;
      }
    },

    openStockLogReport() {
      const cID =
        Number(this.$store.state.userData.cId) ||
        Number(this.filterObj.campus) ||
        0;
      if (!cID) {
        this.$bvToast.toast("Campus is required to generate this report.", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      const product = (this.products || []).find(
        (p) => Number(p.id) === Number(this.stockLogFilters.productID)
      );
      const category = (this.categories || []).find(
        (c) => Number(c.id) === Number(this.stockLogFilters.categoryID)
      );
      const warehouse = (this.warehouses || []).find(
        (w) => Number(w.id) === Number(this.stockLogFilters.warehouseID)
      );
      const supplier = (this.suppliers || []).find(
        (s) => Number(s.id) === Number(this.stockLogFilters.supplierID)
      );

      const query = {
        db: this.$store.state.userData.db,
        cID: String(cID),
      };
      if (this.stockLogFilters.productID) {
        query.productID = String(this.stockLogFilters.productID);
        query.productName = this.productOptionLabel(product) || "";
      }
      if (this.stockLogFilters.categoryID) {
        query.categoryID = String(this.stockLogFilters.categoryID);
        query.categoryName = (category && category.name) || "";
      }
      if (this.stockLogFilters.warehouseID) {
        query.warehouseID = String(this.stockLogFilters.warehouseID);
        query.warehouseName = (warehouse && warehouse.name) || "";
      }
      if (this.stockLogFilters.supplierID) {
        query.supplierID = String(this.stockLogFilters.supplierID);
        query.supplierName = (supplier && supplier.name) || "";
      }
      if (this.stockLogFilters.status) {
        query.status = this.stockLogFilters.status;
      }
      if (this.stockLogFilters.dateFrom) {
        query.dateFrom = this.stockLogFilters.dateFrom;
      }
      if (this.stockLogFilters.dateTo) {
        query.dateTo = this.stockLogFilters.dateTo;
      }

      const routeData = this.$router.resolve({
        name: "stock-log-report-print",
        query,
      });
      window.open(routeData.href, "_blank");
      this.stockLogModal = false;
    },

    showReport() {
      // myskoolpublicschool uses Show Log Report → openStockLogModal (not this)
      if (this.supportsStockLogReport) return;

      // Only validate dates if not these report types
      if (
        !["stock-report", "voucher-summary-report"].includes(
          this.filterObj.type
        )
      ) {
        if (!this.filterObj.fromDate || !this.filterObj.toDate) {
          this.$bvToast.toast("Please select the date correctly", {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          });
          return;
        }
      }

      let dtfrom = this.filterObj.fromDate ? this.filterObj.fromDate : "";
      let dtto = this.filterObj.toDate ? this.filterObj.toDate : "";

      const reportUrl =
        `https://${this.reportDomain}.myskool.app/Inventory/Show?` +
        `type=${this.filterObj.type}` +
        `&dbb=${this.$store.state.userData.db}` +
        `&cID=${this.filterObj.campus}` +
        `&dtFrom=${dtfrom}` +
        `&dtTo=${dtto}`;

      window.open(reportUrl, "_blank");
    },
    async deleteOrder(order) {
      const result = await this.$swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        this.dataLoading = true;
        try {
          const obj = {
            url:
              this.$store.state.domain +
              "posOrders/DeleteOrder?db=" +
              this.$store.state.userData.db +
              "&id=" +
              order.id +
              "&userID=" +
              this.$store.state.userData.userID,
            token: this.$store.state.userData.token,
          };
          await this.get(obj);
          this.$bvToast.toast("Order deleted successfully", {
            title: this.$t("Success"),
            variant: "success",
            solid: true,
          });
        } catch (e) {
          console.error("deleteOrder error:", e);
        } finally {
          // Always reload so the deleted row disappears from the table
          this.LoadData();
        }
      }
    },
  },
};
</script>
<style lang=""></style>
